import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import { Input } from "@vv/ui";
import { fetchTeams, useTeamsDispatch, useTeamsSelector } from "@vv/teams-frontend";
import { useEffect } from "react";
import {
  ALL_VALIDATION_TYPES,
  VALIDATION_TYPE_LABELS,
  type ValidationType,
} from "../../domain/request.types";
import type { WizardFormState } from "./useWizardState";

export interface StepDetailsProps {
  form: WizardFormState;
  update: <K extends keyof WizardFormState>(key: K, value: WizardFormState[K]) => void;
}

/**
 * Team, validation type, title, description - exactly what the Requestor
 * spec step needs. Priority and expected completion date are deliberately
 * absent here: per the approved workflow, the Validation Manager sets those
 * later, when accepting the request - not the Requestor at submission time.
 */
export function StepDetails({ form, update }: StepDetailsProps) {
  const teamsDispatch = useTeamsDispatch();
  const teams = useTeamsSelector((state) => state.teams.teams);

  useEffect(() => {
    teamsDispatch(fetchTeams());
  }, [teamsDispatch]);

  useEffect(() => {
    if (!form.teamId && teams.length === 1) {
      update("teamId", teams[0]!.id);
    }
  }, [teams, form.teamId, update]);

  return (
    <Stack spacing={2.5} sx={{ pt: 1, maxWidth: 560 }}>
      <Input
        select
        label="Requesting team"
        value={form.teamId}
        onChange={(event) => update("teamId", event.target.value)}
        required
      >
        {teams.length === 0 ? <MenuItem value="">No teams available</MenuItem> : null}
        {teams.map((team) => (
          <MenuItem key={team.id} value={team.id}>
            {team.name}
          </MenuItem>
        ))}
      </Input>

      <Input
        select
        label="Validation type"
        value={form.validationType}
        onChange={(event) => update("validationType", event.target.value as ValidationType)}
        required
      >
        {ALL_VALIDATION_TYPES.map((type) => (
          <MenuItem key={type} value={type}>
            {VALIDATION_TYPE_LABELS[type]}
          </MenuItem>
        ))}
      </Input>

      <Input
        label="Title"
        value={form.title}
        onChange={(event) => update("title", event.target.value)}
        required
        autoFocus
      />

      <Input
        label="Description"
        value={form.description}
        onChange={(event) => update("description", event.target.value)}
        multiline
        minRows={4}
        helperText="Describe what needs to be validated and why."
      />
    </Stack>
  );
}
